// Logique PURE de la scène de la Mine (PROFONDEUR 21/07) : DONNÉES de la salle = TROIS sols de
// briques creusables SUPERPOSÉS (étages). ZÉRO dépendance DOM/canvas/réseau (n'importe que
// shared/, lui-même pur) -> testable en node (tests/mine-scene). Le contrôleur client (mine.ts)
// rend ces données via mine-render et câble le creusage à la grande révélation + l'API Lot B.
// Aucune modif du moteur (sim/state) ni de rooms/prefs.
import { type DecorGrid } from '../shared/decor'
import { COLS } from '../shared/types'

// La salle PROFONDE : caverne en haut (le parallaxe premium y vit), puis 3 étages « cavité de
// marche + sol de briques », puis le béton du fond. On DESCEND en creusant une case du sol et en
// SAUTANT dans le trou (chute normale du moteur, règle « Centre ») ; le trou se REBOUCHE tout
// seul par la repousse naturelle (REGROW_TICKS) — plus de rescellement forcé. On REMONTE par
// l'échelle de la colonne 1, qui traverse sols et cavités (rows 13-16).
export const MINE_ROWS_TOTAL = 24
export const WALK_ROW = 12              // marche de l'étage 1 (le spawn y vit)
export const FLOOR_ROW = WALK_ROW + 1   // 13 : sol de l'étage 1 (compat : le « sol » historique)
export const DEPTH_FLOORS = [13, 15, 17] as const // rangées des 3 sols creusables (étages 1/2/3)
export const LADDER_COL = 1             // colonne de l'échelle de remontée
export const SPAWN_COL = 2              // départ du coureur (posé sur le sol de l'étage 1)

/** Salle de la Mine : grille ASCII 28×24. Murs latéraux en béton, caverne d'air au-dessus, puis
 *  3 étages (cavité + sol de briques cols 2..26), échelle col 1 (rows 13-16), béton au fond.
 *  Un unique départ '1' sur la marche de l'étage 1. RECTANGULAIRE (MINE_ROWS_TOTAL×COLS). */
export function mineRows(): string[] {
  const wall = (inner: string): string => '=' + inner + '='
  const air = wall('.'.repeat(COLS - 2))
  const rows: string[] = []
  for (let r = 0; r < WALK_ROW; r++) rows.push(air)              // caverne (air)
  const walk1 = air.split('')
  walk1[SPAWN_COL] = '1'                                          // départ, sur le sol É1
  rows.push(walk1.join(''))                                       // 12 : marche É1
  rows.push(wall('H' + '#'.repeat(COLS - 3)))                     // 13 : sol É1 + échelle col 1
  rows.push(wall('H' + '.'.repeat(COLS - 3)))                     // 14 : cavité É2 + échelle
  rows.push(wall('H' + '#'.repeat(COLS - 3)))                     // 15 : sol É2 + échelle
  rows.push(wall('H' + '.'.repeat(COLS - 3)))                     // 16 : cavité É3 + échelle
  rows.push(wall('=' + '#'.repeat(COLS - 3)))                     // 17 : sol É3 (pied d'échelle en béton)
  for (let r = 18; r < MINE_ROWS_TOTAL; r++) rows.push('='.repeat(COLS)) // fond de béton
  return rows
}

/** Grille de décor de la Mine : VIDE. Le renderer dédié (mine-render) peint tout l'univers ;
 *  la grande révélation est la vedette. (Signature conservée pour le contrôleur.) */
export function mineDecor(): DecorGrid {
  return new Uint8Array(MINE_ROWS_TOTAL * COLS)
}

// --- La POSE du héros dans la Mine (26/07) ------------------------------------------------------
// Règle PURE, miroir de celle du jeu (client/render.ts) : mêmes priorités, mêmes fenêtres. Elle
// vivait auparavant en dur dans mine-render, amputée du dash et de l'échelle — le dash s'exécutait
// donc pour de vrai sans jamais se voir. Ici, elle est tenue par tests/mine-dash.test.ts.

/** Répertoire de poses de la Mine (sous-ensemble des poses du Baroudeur : ni corde ni trampoline). */
export type MinePose = 'idle' | 'run' | 'fall' | 'climb' | 'pickUp' | 'pickHit' | 'crouch' | 'dash' | 'brake'

/** Durée de la pose « freinage talons » après le burst, en ticks (miroir de BRAKE_POSE_TICKS). */
export const MINE_BRAKE_TICKS = 5
/** Durée de l'animation de pioche, en ticks (miroir de DIG_ANIM_TICKS) : le coup se lit en 2 temps. */
export const MINE_DIG_TICKS = 6

export interface MinePoseInputs {
  tick: number
  falling: boolean
  moving: boolean
  onLadder: boolean
  /** Ticks restants d'animation de pioche (0 = pas de creusage en cours). */
  digLeft: number
  /** Fin de la charge accroupie du dash (tick), 0 si aucun dash. */
  dashChargeUntil: number
  /** Fin du burst du dash (tick), 0 si aucun dash n'a jamais été déclenché. */
  dashUntil: number
}

/**
 * Pose à dessiner pour le héros de la Mine. Le DASH prime sur tout le reste (un dash déclenché
 * au-dessus d'un trou doit se lire comme un dash, pas comme une chute), puis la pioche, puis
 * l'échelle, puis la chute, la marche et le repos.
 */
export function minePose(i: MinePoseInputs): MinePose {
  if (i.dashChargeUntil > i.tick) return 'crouch' // 1. la charge : accroupi, l'anticipation
  if (i.dashUntil > i.tick) return 'dash'         // 2. le burst : la fente lancée
  // 3. le freinage talons, seulement avec un appui sous les pieds. `dashUntil > 0` écarte le
  // freinage fantôme des premiers ticks d'une partie où personne n'a encore dashé.
  if (i.dashUntil > 0 && i.tick < i.dashUntil + MINE_BRAKE_TICKS && !i.falling && !i.onLadder) return 'brake'
  if (i.digLeft > 0) return i.digLeft > MINE_DIG_TICKS / 2 ? 'pickHit' : 'pickUp'
  if (i.onLadder) return 'climb' // l'échelle de remontée, empruntée après CHAQUE descente
  if (i.falling) return 'fall'
  return i.moving ? 'run' : 'idle'
}
